import { useEffect, useRef } from 'react';

/**
 * Detect when the user's mouse exits the top of the viewport (intent to leave).
 * Desktop-only; ignored on touch devices where mouseleave is unreliable.
 *
 * @param {() => void} onIntent - called once when intent is detected
 * @param {object} opts
 * @param {boolean} opts.enabled - master switch
 * @param {number} opts.threshold - y-position (px) below the top edge to trigger
 * @param {number} opts.minDelayMs - minimum time on page before listening
 */
export default function useExitIntent(onIntent, opts = {}) {
    const { enabled = true, threshold = 20, minDelayMs = 0 } = opts;
    const firedRef = useRef(false);
    const onIntentRef = useRef(onIntent);
    onIntentRef.current = onIntent;

    useEffect(() => {
        if (!enabled) return undefined;
        if (typeof window === 'undefined') return undefined;

        // Touch devices don't have a reliable mouseleave-from-top — skip.
        const isTouch = window.matchMedia && window.matchMedia('(hover: none)').matches;
        if (isTouch) return undefined;

        let listening = false;
        let startedAt = Date.now();

        const handle = (e) => {
            if (firedRef.current) return;
            if (Date.now() - startedAt < minDelayMs) return;
            // Only when the cursor crosses the top edge
            if ((e.clientY ?? 999) > threshold) return;
            // relatedTarget being null on document mouseleave is the classic exit-intent signal
            if (e.relatedTarget != null) return;
            firedRef.current = true;
            try { onIntentRef.current && onIntentRef.current(); } catch (_e) { /* swallow */ }
        };

        const start = () => {
            if (listening) return;
            listening = true;
            document.addEventListener('mouseleave', handle);
        };

        const timer = setTimeout(start, minDelayMs);

        return () => {
            clearTimeout(timer);
            if (listening) document.removeEventListener('mouseleave', handle);
        };
    }, [enabled, threshold, minDelayMs]);

    return {
        reset: () => { firedRef.current = false; },
    };
}
